import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react";

import { useFirebase } from "firebase";
import { NetworkError, useGET } from "hooks/usePublicAPI";

export type User = {
  id: string;
  person_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  timezone: string | null;
  locale: string | null;
  auth_providers: string[];
  created_at: string;
  updated_at: string;
};

export type UserApiResponse = {
  success: boolean;
  user: User | null;
  error?: string;
};

export type ApiUser = {
  id: string;
  person_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  timezone: string | null;
  locale: string | null;
  auth_providers: string[];
  created_at: string;
  updated_at: string;
};

export type PostHogIdentityStatus =
  "anonymous" | "loading" | "identified" | "failed";

export interface UserContextType {
  user: ApiUser | null;
  loading: boolean;
  error: NetworkError | undefined;
  // True once PostHog can be safely consulted for feature flags: anonymous
  // visitors as soon as the SDK is up, authenticated visitors only after
  // `identify` has run and the next flag reload has landed.
  posthogFlagsReady: boolean;
  posthogIdentityStatus: PostHogIdentityStatus;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { user: firebaseUser, loading: authLoading, logout } = useFirebase();
  const posthog = usePostHog();

  const swrOptions = { shouldRetryOnError: false, revalidateOnFocus: false };
  const {
    data: meData,
    error: meError,
    isLoading: meLoading,
  } = useGET(firebaseUser ? "/me" : null, undefined, swrOptions) as {
    data: UserApiResponse | undefined;
    error: NetworkError | undefined;
    isLoading: boolean;
  };

  const user: User | null = meData?.success ? meData.user : null;

  // Determine the combined loading state
  // Loading is true if auth is loading OR if auth is done but /me is loading
  const loading = authLoading || (!!firebaseUser && meLoading);

  // Stable scalar deps so SWR revalidations don't re-fire identify on a
  // fresh `/me` object reference for the same person.
  const personId = user?.person_id ?? null;
  const userEmail = user?.email ?? null;
  const userFirstName = user?.first_name ?? "";
  const userLastName = user?.last_name ?? "";
  const userPhone = user?.phone ?? null;
  const userId = user?.id ?? null;
  const hasMeData = meData !== undefined;
  const hasMeError = Boolean(meError);
  const hasUserRecord = Boolean(user);
  const meErrorStatus =
    meError instanceof NetworkError ? meError.statusCode : null;

  const [posthogFlagsReady, setPosthogFlagsReady] = useState(false);
  const [posthogIdentityStatus, setPosthogIdentityStatus] =
    useState<PostHogIdentityStatus>("anonymous");
  // Tracks whether `posthog.identify` has run for the current session.
  // The `onFeatureFlags` subscription below uses this to ignore flag
  // reloads that fire while we're still anonymous, preventing a
  // pre-identify reload from prematurely flipping ready=true.
  const hasIdentifiedRef = useRef(false);
  const hasReportedIdentityFailureRef = useRef(false);

  useEffect(() => {
    if (!posthog || !personId || !userId) return;
    posthog.identify(personId, {
      email: userEmail,
      name: `${userFirstName} ${userLastName}`,
      phone: userPhone,
      user_id: userId,
      person_id: personId,
    });
    hasIdentifiedRef.current = true;
    setPosthogIdentityStatus("identified");
    // For returning users whose persisted distinct id already matches
    // `personId`, `identify` is a no-op and PostHog won't auto-reload
    // flags — force a reload so the `onFeatureFlags` callback always
    // fires and `posthogFlagsReady` can flip true.
    posthog.reloadFeatureFlags();
  }, [
    posthog,
    personId,
    userEmail,
    userFirstName,
    userLastName,
    userPhone,
    userId,
  ]);

  // 401 is excluded — auth-loss path triggers logout, so we wait for Firebase
  // to tear down rather than flipping ready=true against an anonymous distinct
  // id mid-teardown.
  const meUnauthorized = meErrorStatus === 401;
  const meSettledWithoutUser =
    !meUnauthorized && (hasMeError || (hasMeData && !personId));

  useEffect(() => {
    if (!firebaseUser) {
      hasIdentifiedRef.current = false;
      hasReportedIdentityFailureRef.current = false;
      setPosthogIdentityStatus("anonymous");
      setPosthogFlagsReady(true);
      return;
    }

    if (!posthog) {
      setPosthogIdentityStatus("loading");
      setPosthogFlagsReady(false);
      return;
    }

    if (meSettledWithoutUser) {
      setPosthogIdentityStatus("failed");
      setPosthogFlagsReady(false);

      if (!hasReportedIdentityFailureRef.current) {
        hasReportedIdentityFailureRef.current = true;
        Sentry.captureMessage(
          "PostHog identity failed before authenticated route render",
          {
            level: "warning",
            tags: {
              area: "posthog_identity",
            },
            extra: {
              has_user_record: hasUserRecord,
              has_person_id: Boolean(personId),
              has_user_id: Boolean(userId),
              me_error_status: meErrorStatus,
            },
          },
        );
      }
      return;
    }

    setPosthogIdentityStatus(
      hasIdentifiedRef.current ? "identified" : "loading",
    );
    setPosthogFlagsReady(false);
  }, [
    posthog,
    firebaseUser,
    meSettledWithoutUser,
    hasMeData,
    hasMeError,
    hasUserRecord,
    personId,
    userId,
    meErrorStatus,
  ]);

  // Subscribe once on mount so the listener is registered before identify
  // can fire — otherwise the post-identify flag reload could land before
  // we attach and we'd never see the callback.
  useEffect(() => {
    if (!posthog) return;
    const unsubscribe = posthog.onFeatureFlags(() => {
      if (hasIdentifiedRef.current) {
        setPosthogFlagsReady(true);
      }
    });
    return () => {
      unsubscribe?.();
    };
  }, [posthog]);

  useEffect(() => {
    if (!meError) return;
    console.error("UserProvider: Error fetching /me data:", meError);
    if (meUnauthorized) {
      console.warn("UserProvider: Received 401 fetching /me, logging out.");
      logout().catch((err) => {
        console.error("UserProvider: logout failed after 401:", err);
      });
    }
  }, [meError, meUnauthorized, logout]);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        error: meError,
        posthogFlagsReady,
        posthogIdentityStatus,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
